import { atom, useAtomValue } from "jotai";

/**
 * Detection mobile cockpit.
 *
 * Strategie : un FORCAGE explicite d'abord, puis user-agent (vrai mobile only)
 * avec breakpoint fallback (<768px) pour catcher les cas user-agent override
 * (debug, DevTools mobile emulation sans flag UA). Cela evite que les
 * utilisateurs desktop avec fenetre etroite voient l'UI mobile (iPad paysage
 * reste desktop).
 *
 * Lecture cote module : la valeur est resolue UNE FOIS au boot puis exposee
 * comme atom. Pas de reactivite a l'orientation/resize — l'utilisateur d'un
 * vrai mobile reste mobile pour toute la session. Si on a besoin de bascule
 * dynamique plus tard (ex: bouton "force desktop" dans settings), on
 * convertira l'atom en writable.
 *
 * Le breakpoint 768px (md de Tailwind) est volontairement large pour
 * inclure les smartphones recents en paysage (iPhone Pro Max ~932×430,
 * Pixel 8 Pro ~915×412). Tablette paysage (iPad ~1024×768) reste desktop.
 *
 * 🩸 LE FORCAGE — pourquoi il existe. Tant que la seule facon d'entrer dans le
 * shell mobile etait d'ouvrir le cockpit SUR un telephone, personne ne pouvait
 * verifier ce qui s'y passe : ni Kayla depuis son poste, ni une session qui
 * corrige. On corrigeait donc a l'aveugle, ou pas du tout. `?mobile=1` (ou le
 * bouton dans « Taille de l'affichage ») pose le choix et recharge.
 */

const MOBILE_BREAKPOINT_PX = 768;

const MOBILE_UA_REGEX =
  /android|iphone|ipod|iemobile|blackberry|opera mini|opera mobi|webos|windows phone|mobile safari/i;

/** Clé du forçage. `"1"` = mobile, `"0"` = desktop, absente = détection. */
export const MOBILE_FORCE_KEY = "cockpit:mobile-force";

/**
 * La décision, pure et testable — l'ORDRE est ce qui compte.
 *
 * Le forçage l'emporte sur tout, y compris sur un vrai téléphone : c'est ce
 * qui permet de forcer le mode DESKTOP depuis un mobile aussi, et donc de
 * comparer les deux rendus sur le même appareil.
 */
export function deciderMobile(entrees: {
  forcage: string | null;
  userAgent: string;
  largeur: number;
}): boolean {
  if (entrees.forcage === "1") return true;
  if (entrees.forcage === "0") return false;
  if (MOBILE_UA_REGEX.test(entrees.userAgent)) return true;

  // iPadOS 13+ reporte "Macintosh" mais a `maxTouchPoints > 1` — on garde
  // ipad en desktop, c'est l'experience attendue (large ecran).

  // Fallback breakpoint : utile pour DevTools mobile emulation sans
  // override UA, ET pour les rares mobiles modernes qui mentent leur UA.
  return entrees.largeur < MOBILE_BREAKPOINT_PX;
}

/**
 * Le forçage courant, en lisant l'URL AVANT le stockage.
 *
 * `?mobile=1` / `?mobile=0` s'écrit dans le stockage au passage : un lien
 * partagé bascule la session entière, et le rechargement suivant la garde.
 * `?mobile=auto` efface le forçage.
 */
function lireForcage(): string | null {
  let stocke: string | null = null;
  try {
    stocke = window.localStorage.getItem(MOBILE_FORCE_KEY);
  } catch {
    /* stockage refusé (navigation privée) — on continue sans */
  }
  let param: string | null = null;
  try {
    param = new URLSearchParams(window.location.search).get("mobile");
  } catch {
    /* URL exotique — on garde le stockage */
  }
  if (param === null) return stocke;
  if (param === "auto") {
    try {
      window.localStorage.removeItem(MOBILE_FORCE_KEY);
    } catch {
      /* idem */
    }
    return null;
  }
  const v = param === "1" || param === "true" ? "1" : "0";
  try {
    window.localStorage.setItem(MOBILE_FORCE_KEY, v);
  } catch {
    /* idem */
  }
  return v;
}

function detectMobileAtBoot(): boolean {
  if (typeof window === "undefined") return false; // SSR / Vite dev pre-mount
  return deciderMobile({
    forcage: lireForcage(),
    userAgent: window.navigator?.userAgent ?? "",
    largeur: window.innerWidth,
  });
}

/** L'appareil PHYSIQUE, sans tenir compte du forçage. */
function detectAppareilAtBoot(): boolean {
  if (typeof window === "undefined") return false;
  return deciderMobile({
    forcage: null,
    userAgent: window.navigator?.userAgent ?? "",
    largeur: window.innerWidth,
  });
}

const isMobileBootValue = detectMobileAtBoot();
const surTelephoneBootValue = detectAppareilAtBoot();

/**
 * Atom mobile/desktop — frozen at boot. Pas de re-eval pour eviter le thrash
 * sur orientation change (un user mobile reste mobile, on ne flip pas).
 */
export const isMobileAtom = atom<boolean>(isMobileBootValue);

/** Hook lecture : `true` si on est sur vrai mobile. */
export function useIsMobile(): boolean {
  return useAtomValue(isMobileAtom);
}

/** Lecture imperative hors-React (mutations, helpers). Source = boot value. */
export function isMobileSync(): boolean {
  return isMobileBootValue;
}

/**
 * Est-on sur un VRAI téléphone ? Le forçage n'y change rien.
 *
 * 🩸 À distinguer de `useIsMobile()`, qui dit quel SHELL rendre. La nuance
 * n'est pas théorique : le blocage des rôles en kiosque (`resolveMobileGate`)
 * existe parce que l'APPAREIL est un téléphone — cadence de chat, densité,
 * analytics de shift — pas parce qu'on affiche le shell mobile. Le lire sur
 * `isMobile` a ouvert une porte dérobée le 28-08-2026 : `?mobile=0` sur un
 * téléphone rendait le shell desktop, donc le gate répondait « allow », donc
 * un chatteur passait. La règle porte sur le matériel ; on lit le matériel.
 */
export function surUnTelephone(): boolean {
  return surTelephoneBootValue;
}

/** Le forçage actif, pour que le réglage sache quoi cocher. */
export function forcageMobileActuel(): "1" | "0" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(MOBILE_FORCE_KEY);
    return v === "1" || v === "0" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Pose (ou retire) le forçage, puis RECHARGE.
 *
 * 🪤 Le rechargement n'est pas de la paresse : `isMobileSync()` est figé au
 * boot et lu hors React (ouverture d'app, onglets, raccourcis). Basculer
 * l'atom seul laisserait ces appelants sur l'ancienne valeur — un shell mobile
 * qui ouvre les panneaux comme un desktop. Une seule vérité par session.
 */
export function poserForcageMobile(v: "1" | "0" | null): void {
  try {
    if (v === null) window.localStorage.removeItem(MOBILE_FORCE_KEY);
    else window.localStorage.setItem(MOBILE_FORCE_KEY, v);
  } catch {
    /* stockage refusé : le réglage ne survivra pas, mais on recharge quand
       même — l'URL `?mobile=…` reste le chemin de secours. */
  }
  window.location.reload();
}
